<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { removeEmoji } from 'shared/helpers/emoji';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  hashDeIdentidade,
  nomeEhOTelefone,
  padraoDeIdenticon,
} from 'dashboard/helper/cw_custom/identidade';

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  size: {
    type: Number,
    default: 32,
  },
  allowUpload: {
    type: Boolean,
    default: false,
  },
  roundedFull: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: null,
    validator: value =>
      !value || wootConstants.AVAILABILITY_STATUS_KEYS.includes(value),
  },
  inbox: {
    type: Object,
    default: null,
  },
  iconName: {
    type: String,
    default: null,
  },
  hideOfflineStatus: {
    type: Boolean,
    default: false,
  },
  // custom (Fase 3): a chave de IDENTIDADE do avatar — telefone, identificador, id. Serve
  // para duas coisas, e as duas consertam o mesmo defeito: escolher o degrau de cinza e
  // desenhar o identicon de quem não tem nome. Sem ela, o componente cai no nome, que é o
  // comportamento antigo — nenhum ponto de chamada quebra por não passar.
  identityKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['upload', 'delete']);

const { t } = useI18n();

const isImageValid = ref(true);
const fileInput = ref(null);

// custom (redesign Modeloja): a paleta de avatar saiu do arco-íris e virou rampa de neutro
// quente.
//
// O que havia antes: rosa, laranja, cinza, verde-água, índigo e azul sorteados por
// `name.length % 6`. Dois problemas, e o segundo é o que decide.
//   1. É exatamente a paleta que o redesign expurgou do resto do produto — e aqui ela está
//      no elemento mais repetido de todas as telas (lista de conversas, contatos, board).
//   2. **A cor não codifica nada.** Comprimento de nome não é identidade: duas pessoas
//      diferentes com nomes do mesmo tamanho recebem a mesma cor, e a mesma pessoa muda de
//      cor se corrigirem o nome dela. É ruído com aparência de informação.
// Como não havia dado por baixo, não há dado a perder tirando a cor. Quem identifica a
// pessoa são as INICIAIS — e elas ficam mais legíveis sem fundo saturado competindo.
//
// A variação continua existindo (6 degraus) só para os avatares não virarem uma parede
// chapada quando empilham numa lista. Varia luminosidade, nunca matiz.
//
// ⚠️ Estes degraus já foram QUENTES (2026-08-06, manhã), na régua R−G≈3 / G−B≈8, para
// acompanhar o fundo bege. O bege foi revertido na mesma tarde e estes voltaram junto: são
// cinzas NEUTROS (R=G=B). Um neutro quente sobre fundo frio amarela e lê como sujeira —
// a mesma armadilha que o slate frio criava ao contrário, virando lavanda sobre o quente.
// Regra que sobrevive às duas paletas: **o avatar segue a temperatura do fundo**.
const AVATAR_COLORS = {
  dark: [
    ['#272727', '#C4C4C4'],
    ['#2C2C2C', '#C8C8C8'],
    ['#313131', '#CCCCCC'],
    ['#363636', '#D0D0D0'],
    ['#3B3B3B', '#D4D4D4'],
    ['#404040', '#D8D8D8'],
  ],
  light: [
    ['#EDEDED', '#494949'],
    ['#E7E7E7', '#464646'],
    ['#E0E0E0', '#434343'],
    ['#D8D8D8', '#404040'],
    ['#CFCFCF', '#3D3D3D'],
    ['#C5C5C5', '#3A3A3A'],
  ],
  default: { bg: '#E7E7E7', text: '#464646' },
};

// custom (redesign Modeloja): presença monocromática — online = ink (sem verde);
// busy = amber (atenção, acento sancionado); offline = cinza.
const STATUS_CLASSES = computed(() => ({
  online: 'bg-n-slate-12',
  busy: 'bg-n-amber-10',
  ...(props.hideOfflineStatus ? {} : { offline: 'bg-n-slate-10' }),
}));

const showDefaultAvatar = computed(() => !props.src && !props.name);

const initials = computed(() => {
  if (!props.name) return '';
  const words = removeEmoji(props.name).split(/\s+/);
  return words.length === 1
    ? words[0].charAt(0).toUpperCase()
    : words
        .slice(0, 2)
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase();
});

// custom (Fase 3): a chave de identidade, com queda para o nome.
const chaveDeIdentidade = computed(() => props.identityKey || props.name || '');

// custom (Fase 3): quem não tem NOME não tem iniciais — tem o próprio número no lugar do
// nome, e as iniciais viram "+5" para todo mundo. Medido em produção: 39 de 61 contatos de
// uma caixa nessa situação. Para esses, o identicon é o único sinal de identidade que existe.
const semNome = computed(() => nomeEhOTelefone(props.name, props.identityKey));

const mostrarIdenticon = computed(
  () => !props.src && !showDefaultAvatar.value && semNome.value
);

// Já filtrado: só as células ACESAS, achatadas em {x, y}. A grade booleana com `v-if` dentro
// do `v-for` é o que o lint do Vue proíbe — e com razão, porque renderizaria 25 nós por
// avatar para apagar metade depois. Numa lista de conversas isso é milhares de nós à toa.
const celulasDoIdenticon = computed(() => {
  if (!mostrarIdenticon.value) return [];

  return padraoDeIdenticon(chaveDeIdentidade.value).flatMap((linha, y) =>
    linha.flatMap((aceso, x) => (aceso ? [{ x, y }] : []))
  );
});

// O degrau de cinza sai do HASH DA IDENTIDADE, não de `name.length`.
//
// O defeito que isto conserta está escrito no comentário da paleta, logo acima: comprimento
// de nome não é identidade — duas pessoas com nomes do mesmo tamanho recebiam o mesmo
// degrau, e a MESMA pessoa mudava de degrau quando alguém corrigia o nome dela. O telefone
// não muda quando o cadastro é editado.
const getColorsByNameLength = computed(() => {
  if (!chaveDeIdentidade.value) return AVATAR_COLORS.default;

  const index =
    hashDeIdentidade(chaveDeIdentidade.value) % AVATAR_COLORS.light.length;
  return {
    bg: AVATAR_COLORS.light[index][0],
    darkBg: AVATAR_COLORS.dark[index][0],
    text: AVATAR_COLORS.light[index][1],
    darkText: AVATAR_COLORS.dark[index][1],
  };
});

const containerStyles = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
}));

const borderRadiusClass = computed(() => {
  if (props.roundedFull) {
    return 'rounded-full';
  }

  // Approximates 25% of size
  if (props.size <= 16) return 'rounded'; // 4px
  if (props.size <= 24) return 'rounded-md'; // 6px
  if (props.size <= 32) return 'rounded-lg'; // 8px
  if (props.size <= 48) return 'rounded-xl'; // 12px
  return 'rounded-2xl'; // 16px
});

const avatarStyles = computed(() => ({
  ...containerStyles.value,
  backgroundColor:
    !showDefaultAvatar.value && (!props.src || !isImageValid.value)
      ? getColorsByNameLength.value.bg
      : undefined,
  color:
    !showDefaultAvatar.value && (!props.src || !isImageValid.value)
      ? getColorsByNameLength.value.text
      : undefined,
  '--dark-bg': getColorsByNameLength.value.darkBg,
  '--dark-text': getColorsByNameLength.value.darkText,
}));

const badgeStyles = computed(() => {
  const badgeSize = Math.max(props.size * 0.35, 8); // 35% of avatar size, minimum 8px
  return {
    width: `${badgeSize}px`,
    height: `${badgeSize}px`,
    top: `${props.size - badgeSize / 1.1}px`,
    left: `${props.size - badgeSize / 1.1}px`,
  };
});

const iconStyles = computed(() => ({
  fontSize: `${props.size / 1.6}px`,
}));

const initialsStyles = computed(() => ({
  fontSize: `${Math.min(props.size / 2.5, 24)}px`,
}));

const invalidateCurrentImage = () => {
  isImageValid.value = false;
};

const handleUploadAvatar = () => {
  fileInput.value.click();
};

const handleImageUpload = event => {
  const [file] = event.target.files;
  if (file) {
    emit('upload', {
      file,
      url: file ? URL.createObjectURL(file) : null,
    });
  }
};

const handleDeleteAvatar = () => {
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  emit('delete');
};

const handleDismiss = event => {
  event.stopPropagation();
  handleDeleteAvatar();
};

watch(
  () => props.src,
  () => {
    isImageValid.value = true;
  }
);
</script>

<template>
  <span
    class="relative inline-flex group/avatar z-0 flex-shrink-0 align-middle"
    :style="containerStyles"
  >
    <!-- Status Badge -->
    <slot name="badge" :size="size">
      <div
        v-if="status && STATUS_CLASSES[status]"
        class="absolute z-20 border rounded-full border-n-slate-3"
        :style="badgeStyles"
        :class="STATUS_CLASSES[status]"
      />
      <div
        v-if="inbox && !(status && STATUS_CLASSES[status])"
        :style="badgeStyles"
        class="absolute z-20 flex items-center justify-center rounded-full bg-n-solid-1 border border-transparent flex-shrink-0"
      >
        <ChannelIcon :inbox="inbox" class="w-full h-full text-n-slate-11" />
      </div>
    </slot>

    <!-- Delete Avatar Button -->
    <div
      v-if="src && allowUpload"
      class="absolute z-20 flex items-center justify-center invisible w-6 h-6 transition-all duration-300 ease-in-out opacity-0 cursor-pointer outline outline-1 outline-n-container -top-2 ltr:-right-2 rtl:-left-2 rounded-xl bg-n-solid-3 group-hover/avatar:visible group-hover/avatar:opacity-100"
      @click="handleDismiss"
    >
      <Icon icon="i-lucide-x" class="text-n-slate-11 size-4" />
    </div>

    <!-- Avatar Container -->
    <span
      role="img"
      class="relative inline-flex items-center justify-center object-cover overflow-hidden font-medium outline outline-1 -outline-offset-1 outline-[rgb(0_0_0_/_0.03)] dark:outline-[rgb(255_255_255_/_0.04)]"
      :class="[
        borderRadiusClass,
        {
          'dark:!bg-[var(--dark-bg)] dark:!text-[var(--dark-text)]':
            !showDefaultAvatar && (!src || !isImageValid),
          'bg-n-slate-3 dark:bg-n-slate-4': showDefaultAvatar,
        },
      ]"
      :style="avatarStyles"
    >
      <!-- Avatar Content -->
      <img
        v-if="src && isImageValid"
        :src="src"
        :alt="name"
        @error="invalidateCurrentImage"
      />

      <template v-else>
        <!-- Custom Icon -->
        <Icon v-if="iconName" :icon="iconName" :style="iconStyles" />

        <!-- custom (Fase 3): IDENTICON para quem não tem nome.
             A Meta não entrega foto de perfil de cliente a plataforma nenhuma — é limitação
             dela, e o único contorno que existe no mundo é cliente NÃO-OFICIAL de WhatsApp
             Web, que já custou um número banido nesta operação. Então, para o contato que
             chega só com número, a identidade é CONSTRUÍDA: marca simétrica, determinística
             pelo telefone, sem matiz (a política manda: "identidade nossa = hueless, por
             hash"). Sem isto, todos eles mostram o mesmo "+5". -->
        <svg
          v-else-if="mostrarIdenticon"
          :width="size"
          :height="size"
          viewBox="0 0 5 5"
          shape-rendering="crispEdges"
          aria-hidden="true"
          class="select-none"
        >
          <rect
            v-for="celula in celulasDoIdenticon"
            :key="`c${celula.y}-${celula.x}`"
            :x="celula.x"
            :y="celula.y"
            width="1"
            height="1"
            fill="currentColor"
          />
        </svg>

        <!-- Initials -->
        <span
          v-else-if="!showDefaultAvatar"
          :style="initialsStyles"
          class="select-none"
        >
          {{ initials }}
        </span>

        <!-- Fallback Icon if no name or image -->
        <Icon
          v-else
          :title="t('THUMBNAIL.AUTHOR.NOT_AVAILABLE')"
          icon="i-lucide-user"
          :style="iconStyles"
        />
      </template>

      <!-- Upload Overlay and Input -->
      <slot
        v-if="allowUpload || $slots.overlay"
        name="overlay"
        :size="size"
        :handle-upload="handleUploadAvatar"
        :file-input-ref="fileInput"
        :handle-image-upload="handleImageUpload"
      >
        <div
          class="absolute inset-0 z-10 flex items-center justify-center invisible w-full h-full transition-all duration-300 ease-in-out opacity-0 bg-n-alpha-black1 group-hover/avatar:visible group-hover/avatar:opacity-100"
          :class="borderRadiusClass"
          @click="handleUploadAvatar"
        >
          <Icon
            icon="i-lucide-upload"
            class="text-white"
            :style="{ width: `${size / 2}px`, height: `${size / 2}px` }"
          />
          <input
            v-if="allowUpload"
            ref="fileInput"
            type="file"
            accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
            class="hidden"
            @change="handleImageUpload"
          />
        </div>
      </slot>
    </span>
  </span>
</template>
